import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { prepareModelToSubmit } from 'app/form/form.utils';
import {
  ISPNotificationService,
  INotification,
  INotificationEvent,
} from 'app/services/notification.service';
import { IFormButtonUi } from 'components/form-button';
import {
  combineLatest,
  from,
  Observable,
  of,
  Subject,
  Subscription,
  zip,
} from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { ConfirmOptions, ISPUIConfirmManager } from '@ispui/confirm';
import { WINDOW, WindowWrapper } from '@ngispui/window-service';

import { IFormModel } from 'common/dynamic-form/dynamic-form.interface';
import {
  createProgressIdForDownloadList,
  createProgressIdForFormSubmitting,
  isWaitProgressTypeById,
} from 'common/progress-task-modal/progress-task.utils';
import { convertParamsToStringWithEncode } from 'utils/convert-params-to-string';
import { convertStringToParamsWithDecode } from 'utils/convert-string-to-params';
import { DocHelper } from 'utils/dochelper';
import { getRedirectTypeOrDoRedirect } from 'utils/get-redirect-type-or-do-redirect';
import { hasFile } from 'utils/has-file';

import {
  IBanner,
  IDocument,
  INotificationBanner,
  IOk,
  IToolbarAction,
  IToolButton,
  OkType,
  TActionType,
  TStringBool,
} from './api5-service/api.interface';
import { Api5Service } from './api5-service/api5.service';
import { IHttpParam } from './http-base-service/http-base.interface';
import { IndicateRebootService } from './indicate-reboot.service';
import {
  PreloadedActionOptions,
  PreloadedActionService,
} from './preloaded-action.service';
import { ProgressBarService } from './progressbar-service/progressbar.service';
import { Tab } from './tab/tab.class';
import { TabService } from './tab/tab.service';

import { AppService } from '../app.service';

const CONFIRM_RENDER_TIMEOUT = 50;

const CONFIRM_TOP_OFFSET = 10;

const CONFIRM_LEFT_OFFSET = 50;

/**
 * Action service
 * Manage action open
 */
@Injectable({
  providedIn: 'root',
})
export class ActionService {
  /** cache form reponse for "wait" progress */
  private readonly responseCache: Record<
    string,
    {
      response: IDocument;
      dataSource: {
        tab: Tab;
        params: Params;
        options: PreloadedActionOptions;
      };
    }
  > = {};

  /** class for control confirmBox */
  private readonly confirmManager = new ISPUIConfirmManager();

  /** errror banner notification */
  private readonly bannerMap: Record<string, Subscription> = {};

  /** subject for cancel prev request */
  private readonly cancelMenuRequest$ = new Subject<void>();

  constructor(
    private readonly tabService: TabService,
    private readonly appService: AppService,
    @Inject(WINDOW) private readonly window: WindowWrapper,
    private readonly preloadedActionService: PreloadedActionService,
    private readonly progressbarService: ProgressBarService,
    private readonly notificationService: ISPNotificationService,
    private readonly api5Service: Api5Service,
    private readonly rebootService: IndicateRebootService,
  ) {}

  /**
   * Gets the description for a confirm window
   *
   * @param toolbtn - pressed toolbar button
   * @param tab - tab instance
   * @param doc - doc instance
   */
  private getConfirmMessage(
    toolbtn: Pick<
      IToolButton,
      '$func' | '$name' | '$sametab' | '$type' | '$warning'
    >,
    tab: Tab,
    doc: IDocument,
  ): string {
    return (
      DocHelper.getMessage(`msg_confirm_${toolbtn.$name}`, doc) ||
      DocHelper.getMessage(`msg_confirm_${toolbtn.$name}`, tab.doc)
    );
  }

  /**
   * Open confirm box and update tab after request
   *
   * @param confirmOptions - map with confirmBox options
   * @param params - param for request
   * @param tab - tab instance
   * @param canRefreshTab - whether the tab should be refreshed after resolving
   */
  private openConfirm(
    confirmOptions: ConfirmOptions,
    params: Params,
    tab: Tab,
    canRefreshTab = true,
  ): Observable<IDocument> {
    return from(this.confirmManager.open(confirmOptions)).pipe(
      filter(confirmResult => confirmResult.resolve),
      mergeMap(() => this.preloadedActionService.postAction(params)),
      switchMap(actionResult =>
        combineLatest([
          of(actionResult),
          canRefreshTab ? this.tabService.updateFromServer(tab) : of(null),
        ]),
      ),
      tap(([actionResult, refreshedTab]) => {
        if (actionResult.banner) {
          this.handleNotificationBanners(
            DocHelper.getNotificationBanners(actionResult),
          );
        }
        if (actionResult.warning) {
          if (Array.isArray(actionResult.warning)) {
            refreshedTab.warning = actionResult.warning;
          } else {
            refreshedTab.warning = [actionResult.warning];
          }
        }
        if (refreshedTab) {
          this.tabService.update(tab, refreshedTab);
        }
      }),
      map(([oldTab, refreshedTab]) => refreshedTab || oldTab),
    );
  }

  /**
   * Calculate confirm position
   *
   * @param anchorElement - link to anchor html element
   */
  private async positionConfirm(anchorElement: HTMLElement): Promise<void> {
    if (!anchorElement) {
      return;
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    await new Promise(resolve => setTimeout(resolve, CONFIRM_RENDER_TIMEOUT));
    const confirmContainer: HTMLElement = document.querySelector(
      '.ispui-confirm__container',
    );
    if (!confirmContainer) {
      return;
    }

    const confirmRect = confirmContainer.getBoundingClientRect();

    confirmContainer.style.position = 'absolute';
    if (
      confirmRect.height <
      document.documentElement.clientHeight -
        anchorRect.bottom -
        CONFIRM_TOP_OFFSET
    ) {
      confirmContainer.style.top = `${
        anchorRect.bottom + CONFIRM_TOP_OFFSET
      }px`;
    }

    if (
      document.documentElement.clientWidth - anchorRect.left >
      confirmRect.width - CONFIRM_LEFT_OFFSET
    ) {
      confirmContainer.style.left = `${
        anchorRect.left - CONFIRM_LEFT_OFFSET
      }px`;
    } else {
      confirmContainer.style.left = `${
        document.documentElement.clientWidth - confirmRect.width
      }px`;
    }
  }

  /**
   * Returns has metadata type for render tab
   *
   * @param doc - doc instance
   */
  private hasMetaData(doc: IDocument): boolean {
    if (doc.$func === 'dashboard') {
      return true;
    }
    const type: string = doc?.metadata?.$type;
    const hasMetaData = ['list', 'form', 'report', 'helpboard'].includes(type);
    return hasMetaData;
  }

  /**
   * Update main menu by attirbute
   *
   * @param ok
   */
  private checkOkAttr(ok: IOk | undefined): void {
    if (ok?.$refreshmenu) {
      this.progressbarService.hideProgressBar();
      this.appService.getDesktop();
    }
  }

  /**
   * Handle response on ok value
   *
   * @param doc - doc instance
   * @param tab - tab instance
   */
  private hasOkRedirect(doc: IDocument, tab: Tab = null): boolean {
    const ok = doc?.ok;
    const redirectType: OkType | false = getRedirectTypeOrDoRedirect(doc);

    this.checkOkAttr(ok);

    switch (redirectType) {
      case OkType.List:
      case OkType.Form: {
        const params = convertStringToParamsWithDecode(ok.$);
        if (tab?.q.table_params) {
          params.table_params = tab.q.table_params;
        }
        // hack for happy filter
        if (
          Object.keys(params).length === 1 &&
          params[Object.keys(params)[0]] === 'undefined'
        ) {
          this.tabService.update(tab);
          return !!redirectType;
        }
        this.preloadedActionService
          .getAction(params, { hideProgressbar: false })
          .subscribe(d => {
            if (this.hasOkRedirect(d, tab) || !this.hasMetaData(d)) {
              return;
            }
            if (tab?.type === 'list' || ok.$child === 'yes') {
              this.tabService.createChild(d, tab?.id);
              // keep parent only for List redirect -> logic from orion
              // keep parent for form to form redirect for ispmgr site create
            } else if (tab && redirectType === OkType.List) {
              // replace current tab
              this.tabService.update(tab, d);
              // hack change after release
            } else if (
              tab?.func === 'site.edit' ||
              tab?.func === 'aps.user_catalog'
            ) {
              this.tabService.update(tab, d);
            } else {
              // if not tab create, for redirect from menu
              this.tabService.create(d);
            }
          });

        break;
      }
      case OkType.Reboot:
        this.rebootService.setRebootModal(ok.$);
        this.progressbarService.hideProgressBar();
        return false;
      default:
        this.progressbarService.hideProgressBar();
        break;
    }
    return !!redirectType;
  }

  /**
   * Find tab by func and set active
   *
   * @param tab - tab instance
   * @param isNew
   */
  private checkTabOnExistAndSetActive(tab: Tab, isNew: boolean): boolean {
    if (tab && (tab.isPin || !isNew)) {
      return true;
    }
    return false;
  }

  /**
   * Handle form response
   *
   * @param doc
   * @param tab - tab instance
   */
  private applyFormSubmitResponse(doc: IDocument, tab: Tab): void {
    if (doc?.ok) {
      if (!this.hasOkRedirect(doc, tab)) {
        this.tabService.close(tab, true);
      }
    } else {
      this.tabService.update(tab, doc);
    }
  }

  /**
   * Shows the confirmation window and downloads the files
   * by making a GET request in new tab if confirmed
   *
   * @param confirmOptions - confirm window parameters (header, desc, etc)
   * @param params - parameters to add to new tab
   * @param cgi
   */
  private openConfirmAndDownload(
    confirmOptions: ConfirmOptions,
    params: Params,
    cgi = '',
  ): void {
    params['ngsw-bypass'] = true;
    from(this.confirmManager.open(confirmOptions)).subscribe(result => {
      if (result.resolve) {
        this.window.open(
          `${this.appService.host}${
            cgi ? cgi : this.appService.binary
          }?${convertParamsToStringWithEncode(params)}`,
          '_download',
        );
      }
    });
  }

  /**
   * Submit form with files. Turn on progress bar preloader
   *
   * @param tab - tab instance. To turn on preloader
   * @param params - post params
   * @param preloadedActionOptions - additional options for post
   */
  private submitFormWithFiles(
    tab: Tab,
    params: Params,
    preloadedActionOptions: PreloadedActionOptions,
  ): Observable<IDocument> {
    return this.preloadedActionService
      .uploadAction(params as IHttpParam, preloadedActionOptions)
      .pipe(
        filter(res => {
          if (res.type === HttpEventType.UploadProgress) {
            const uploadProgress = Math.round((res.loaded / res.total) * 100);
            tab.setUploadFileProgressModal(
              uploadProgress,
              this.appService.getDesktopMessage('msg_form_sending'),
            );
            return false;
          } else {
            tab.setUploadFileProgressModal(null);
            return true;
          }
        }),
        map(res => (res as HttpResponse<IDocument>).body),
      );
  }

  /**
   * Submit form by post action. Automatically determine whether params have files and turn on preloader if so
   *
   * @param tab - tab instance. To turn on preloader
   * @param params - post params
   * @param preloadedActionOptions - additional options for post
   */
  private submitForm(
    tab: Tab,
    params: Params,
    preloadedActionOptions: PreloadedActionOptions,
  ): Observable<IDocument> {
    if (hasFile(params)) {
      return this.submitFormWithFiles(
        tab,
        params as IHttpParam,
        preloadedActionOptions,
      );
    } else {
      return this.preloadedActionService.postAction(
        params as IHttpParam,
        preloadedActionOptions,
      );
    }
  }

  /**
   * Info banner "more" link handler
   *
   * @param banner
   */
  infoBannerHandler(banner: IBanner): void {
    switch (banner.$infotype) {
      case 'url':
      case 'help':
        this.openNewWindow(banner.$info);
        break;
      case 'func':
      case 'formfunc': {
        const params: Params = {
          ...convertStringToParamsWithDecode(`func=${banner.$info}`),
        };
        if (banner.$infoelid) {
          params.elid = banner.$infoelid;
        }
        this.handleAction(params, null, true);
        break;
      }
    }
  }

  /**
   * Send request about close banner
   *
   * @param banner
   */
  dismissBanner(banner: IBanner): void {
    const params: Params = { id: banner.$id };
    if (banner.$infoelid) {
      params.elid = banner.$infoelid;
    }
    this.api5Service.dismiss(params).subscribe();
  }

  /**
   * Redirect to url in this window
   *
   * @param url
   */
  redirectToUrl(url: string): void {
    this.window.open(url, '_self');
  }

  /**
   * Open new browser window
   *
   * @param url
   */
  openNewWindow(url: string): void {
    this.window.open(url, '_blank');
  }

  /**
   * Handle action form menu
   *
   * @param params - params
   * @param type - action type
   * @param isNew - have to open new tab
   */
  handleAction(params: Params, type: TActionType, isNew?: boolean): void {
    switch (type) {
      case 'url':
        this.openNewWindow(params.func);
        break;
      case 'window':
        this.window.open(
          `${this.window.location.origin}/${type}/${params.func}&newwindow=yes`,
        );
        break;
      case 'action':
        this.preloadedActionService.getAction({ ...params }).subscribe(d => {
          this.hasOkRedirect(d);
        });
        break;
      default: {
        if (!isNew) {
          this.cancelMenuRequest$.next();
        }
        const tab = this.tabService.findMainTabByFunc(params.func);
        if (!this.checkTabOnExistAndSetActive(tab, isNew)) {
          this.preloadedActionService
            .getAction({ ...params }, { hideProgressbar: false })
            .pipe(takeUntil(this.cancelMenuRequest$))
            .subscribe(d => {
              if (this.hasOkRedirect(d) || !this.hasMetaData(d)) {
                return;
              }
              this.tabService.create(d, isNew);
            });
        } else {
          this.tabService.setActive(tab);
        }
        break;
      }
    }
  }

  /**
   * Get tab data with new params, used for sort and pagination
   *
   * @param func
   * @param params
   * @param tab - tab instance
   */
  updateTab(func: string, params: Params, tab: Tab): void {
    this.preloadedActionService
      .getAction({ func, ...params })
      .subscribe(doc => {
        if (tab.isActive) {
          this.tabService.update(tab, doc);
        }
      });
  }

  /**
   * Open child tab for current tab w/ params
   *
   * @param params - collection of params
   * @param tab - tab instance
   */
  openChild(params: Params, tab: Tab): void {
    this.preloadedActionService.getAction({ ...params }).subscribe(d => {
      if (this.hasOkRedirect(d, tab) || !this.hasMetaData(d)) {
        return;
      }
      this.tabService.createChild(d, tab.id);
    });
  }

  /**
   * Open new tab w/ given params
   *
   * @param params - collection of params
   * @param blank - create new tab and do not close old one
   */
  open(params: Params, blank = false): void {
    this.preloadedActionService.getAction({ ...params }).subscribe(d => {
      if (this.hasOkRedirect(d) || !this.hasMetaData(d)) {
        return;
      }
      this.tabService.create(d, blank);
    });
  }

  /**
   * Handle action form toolbar
   *
   * @param params - toolbutton params
   * @param params.elidList - entity's ids
   * @param params.toolbtn - toolbtn entity
   * @param params.tab - tab instance
   * @param params.buttonElement - button
   * @param params.progressId - action progress id
   * @param params.additionalParams - additional params for toobtn handling
   * @param params.canRefreshTab - is tab can be refreshed
   * @param params.doc - doc instance
   */
  handleToolBtn({
    elidList,
    toolbtn,
    tab,
    buttonElement,
    progressId,
    additionalParams,
    canRefreshTab,
    doc,
  }: IToolbarAction): Observable<IDocument> {
    if (progressId) {
      tab.state.progressIdSubject.next(progressId);
    } else {
      tab.state.progressIdSubject.next(null);
    }

    const empty = of(null);
    if (!toolbtn.$type) {
      return empty;
    }
    const func = toolbtn.$func;
    const cgi = toolbtn.$cgi;
    const plid = tab.plid;
    const elid = elidList.join(', ');
    const confirmMsg: string = this.getConfirmMessage(toolbtn, tab, doc);

    let params: Params = { func };

    if (doc?.tparams.table_params) {
      params.table_params = doc.tparams.table_params.$;
    }

    if (additionalParams?.tconvert) {
      params = {
        ...params,
        tconvert: additionalParams.tconvert,
      };
    }

    if (progressId) {
      params.progressid = progressId;
    }

    if (additionalParams?.elnameList?.[0]) {
      params.elname = additionalParams.elnameList[0];
    }

    let updateTab = false;
    const sameTab = toolbtn.$sametab === 'yes';
    switch (toolbtn.$type) {
      case 'action':
        if (elid) {
          params.elid = elid;
        }
        if (plid) {
          params.plid = plid;
        }
        updateTab = true;
        break;
      case 'back':
        if (!sameTab) {
          // if go back from reports, do not update the parent tab (taken from orion)
          const refreshParentTab = tab.isChild && tab.type !== 'report';
          this.tabService.close(tab, refreshParentTab);
          return empty;
        } else {
          let pos = plid.lastIndexOf('/');
          if (pos !== -1) {
            const nextPlid = plid.substring(0, pos);
            pos = nextPlid.lastIndexOf('/');
            // root element, has not plid
            if (pos === -1) {
              params.elid = nextPlid;
            } else {
              params.plid = nextPlid.substring(0, pos);
              params.elid = nextPlid.substr(pos + 1);
            }
          }
        }

        break;
      case 'edit':
        if (elid) {
          params.elid = elid;
        }
        if (plid) {
          params.plid = plid;
        }
        if (elidList.length > 1) {
          params.faction = params.func;
          params.func = 'groupedit';
          delete params.elname;
        }
        break;
      case 'groupform':
      case 'editlist':
        if (elid) {
          params.elid = elid;
        }
        if (plid) {
          params.plid = plid;
        }
        break;
      case 'groupformnosel':
      case 'editnosel':
        if (elid) {
          params.elid = elid;
        }
        if (plid !== undefined) {
          params.plid = plid;
        }
        break;
      case 'group': {
        params.elid = elid;
        params.plid = plid;

        const isAllElementsHaveNames =
          additionalParams?.elnameList?.length === elidList.length;

        const confirmElemList = isAllElementsHaveNames
          ? additionalParams.elnameList
          : elidList;

        const maxElemCount = 10;

        let confirmTextContent = confirmElemList
          .slice(0, maxElemCount)
          .map(elname => {
            const maxElnameLength = 50;
            if (elname.length > maxElnameLength) {
              return `${elname.substring(0, maxElnameLength)}...`;
            }
            return elname;
          })
          .join(', ');

        if (confirmElemList.length > maxElemCount) {
          const messageTotal = DocHelper.getMessage('msg_totalelem', doc);
          confirmTextContent += messageTotal.replace(
            '__s__',
            confirmElemList.length.toString(),
          );
        }

        return zip(
          this.openConfirm(
            {
              header:
                toolbtn.$warning === 'yes'
                  ? this.appService.getDesktopMessage('msg_warning')
                  : '',
              description: `${confirmMsg} ${confirmTextContent}?`,
              ok: this.appService.getDesktopMessage('msg_ok'),
              cancel: this.appService.getDesktopMessage('msg_cancel'),
            },
            params,
            tab,
            canRefreshTab,
          ),
          of(null).pipe(
            tap(() => {
              this.positionConfirm(buttonElement);
            }),
          ),
        ).pipe(map(([confirmResult]) => confirmResult));
      }
      case 'groupdownload': {
        params.elid = elid;
        params.plid = plid;
        params.progressid = createProgressIdForDownloadList();

        const description = `${confirmMsg} ${params.elid}?`;
        this.openConfirmAndDownload(
          {
            header: '',
            description,
            ok: this.appService.getDesktopMessage('msg_ok'),
            cancel: this.appService.getDesktopMessage('msg_cancel'),
          },
          params,
          cgi,
        );
        return empty;
      }
      case 'window':
      case 'windownosel':
      case 'groupwindow': {
        const url = cgi ? doc.$host + cgi : doc.$host + doc.$binary;
        this.openNewWindow(
          `${url}?${convertParamsToStringWithEncode({
            func,
            elid,
            plid,
            newwindow: 'yes',
          })}`,
        );
        return empty;
      }
      case 'list':
      case 'new':
        params.plid = plid;
        delete params.elname;
        break;
      case 'preview':
        console.warn("Type preview isn't implemented yet.");
        return empty;
      case 'refresh':
        if (plid !== undefined) {
          params.plid = plid;
        }
        updateTab = true;
        break;
      case 'url': {
        const url = cgi ? cgi : func;
        this.openNewWindow(url);
        return empty;
      }
      default:
        console.warn(`Not support type ${toolbtn.$type} of the button`);
        return empty;
    }
    return this.preloadedActionService.getAction({ ...params }).pipe(
      tap((d: IDocument) => {
        if (this.hasOkRedirect(d, tab) || d?.ok) {
          if (Object.keys(d.ok).length > 0) {
            return;
          }
          // if an empty object came to OK, then backend performed the action and
          // you do not need to go anywhere and you can update the list
          updateTab = true;
        }
        if (!this.hasMetaData(d) && !updateTab) {
          return;
        }
        if (sameTab) {
          this.tabService.openSame(tab, d);
        } else if (updateTab) {
          this.tabService.update(tab);
        } else {
          this.tabService.createChild(d, tab.id);
        }
      }),
    );
  }

  /**
   * Prepare form params and submit form
   *
   * @param params - query params
   * @param params.button
   * @param params.form
   * @param params.tab
   * @param params.emitOnError
   * @param params.additionalParams
   */
  prepareAndSubmitForm$({
    form,
    button,
    tab,
    emitOnError,
    additionalParams,
  }: {
    button?: IFormButtonUi;
    form: IFormModel;
    tab: Tab;
    emitOnError?: boolean;
    additionalParams?: Params;
  }): Observable<any> {
    const params = prepareModelToSubmit({
      form,
      button,
      func: tab.func,
      elid: tab.elid,
      plid: tab.plid,
      table_params: tab.q.table_params,
      sok: true,
      ...additionalParams,
    });

    const preloadedActionOptions: PreloadedActionOptions = {
      action: tab.doc.metadata?.form?.$action,
      // because we implemented server error banner in the form footer
      skipErrorNotification: true,
    };

    if (hasFile(params)) {
      preloadedActionOptions.emitOnError = emitOnError;
    }

    const progressType = tab.doc.metadata?.form?.$progress;
    if (progressType && button && button.$type !== 'back') {
      const progressId = createProgressIdForFormSubmitting(progressType);
      params.progressid = progressId;
      tab.state.progressIdSubject.next(progressId);
    }

    return this.submitForm(tab, params, preloadedActionOptions).pipe(
      tap(response => {
        const isWaitForSomeTask = isWaitProgressTypeById(
          params.progressid as string,
        );

        // if progressType="wait" do nothing, wait will update tab when finish
        if (isWaitForSomeTask) {
          const isBEAcceptSomeTask = response.ok || response.progressok;
          if (isBEAcceptSomeTask) {
            // будь осторожен путник, не каждый поймет этот код и не каждый дойдет до конца
            this.responseCache[tab.pHash] = {
              response,
              dataSource: {
                tab,
                params,
                options: preloadedActionOptions,
              },
            };
            return;
          }
        }
        tab.state.progressIdSubject.next(null);

        this.applyFormSubmitResponse(response, tab);
      }),
    );
  }

  // Handle "cached" form response after finish "wait" progress
  handleResponseAfterProgress(tab: Tab): void {
    const cache = this.responseCache[tab.pHash];
    if (cache) {
      if (cache.response.progressok?.$ === 'ok') {
        delete cache.dataSource.params.progressid;
        this.submitForm(
          cache.dataSource.tab,
          cache.dataSource.params,
          cache.dataSource.options,
        ).subscribe(response => {
          this.applyFormSubmitResponse(response, tab);
        });
        delete this.responseCache[tab.pHash];
      } else {
        this.applyFormSubmitResponse(cache.response, tab);
        tab.state.progressIdSubject.next(null);
      }
      delete this.responseCache[tab.pHash];
    }
  }

  /**
   * Process all notification-like banners, i.e. show these notifications and handle their events
   *
   * @param bannerList - a list of INotificationBanner
   */
  handleNotificationBanners(bannerList: INotificationBanner[]): void {
    bannerList
      .filter(notification => !this.bannerMap[notification.$id])
      .forEach(banner => {
        const notification: INotification = {
          id: banner.$id,
          title: this.appService.getDesktopMessage('msg_error'),
          message: banner.msg?.$ || banner.msg?.[0]?.$,
          status: 'danger',
          data: banner,
        };

        if (banner.$infotype) {
          notification.links = [
            { text: this.appService.getDesktopMessage('msg_moreinfo') },
          ];
        }

        switch (banner.$status) {
          case '1':
            notification.status = 'danger';
            notification.title =
              this.appService.getDesktopMessage('mobile_info');
            break;
          case '2':
            notification.status = 'info';
            notification.title =
              this.appService.getDesktopMessage('mobile_info');
            break;
          default:
            notification.status = 'success';
            notification.title =
              this.appService.getDesktopMessage('msg_finish');
            break;
        }

        this.bannerMap[banner.$id] = this.notificationService
          .showNotification(notification)
          .pipe(
            filter(({ type }) =>
              ['link-click', 'hide', 'close'].includes(type),
            ),
          )
          .subscribe((e: INotificationEvent) => {
            if (e.type === 'close') {
              this.dismissBanner(e.data);
            } else if (e.type === 'link-click') {
              this.infoBannerHandler(e.data);
              this.notificationService.close(e.id);
            }
            if (e?.data?.$id) {
              delete this.bannerMap[e.data.$id];
            }
          });
      });
  }

  /**
   * Handle nested list link new tab opening. Used in some lists cells.
   *
   * @param options - nested list options
   * @param options.elid - row elid
   * @param options.colValue - cell value
   * @param options.colName - col name
   * @param options.tab - tab instance
   * @param options.nestedList - child list func
   * @param options.blank - should child list tab be opened in new tab
   * @param options.parentFilterModel - parent list filter model. Can be absent in case of no filter applied
   */
  handleNestedListLink(options: {
    elid: string;
    colValue: string;
    colName: string;
    tab: Tab;
    nestedList: string;
    blank: TStringBool;
    parentFilterModel?: IFormModel;
  }): void {
    this.open(
      {
        elid: options.elid,
        plid: options.tab.plid,
        col_value: options.colValue,
        col: options.colName,
        nestedlist: options.nestedList,
        func: 'nestedlist',
        pfunc: options.tab.func,
        parentfilter: options.parentFilterModel
          ? convertParamsToStringWithEncode(options.parentFilterModel)
          : undefined,
      },
      options.blank === 'yes',
    );
  }

  /**
   * Handle edit form link new tab opening. Used in some lists cells.
   *
   * @param options - edit form link options
   * @param options.elid - row elid
   * @param options.tab - tab instance
   * @param options.editform - new tab func
   */
  handleEditFormLink(options: {
    elid: string;
    tab: Tab;
    editform: string;
  }): void {
    this.openChild(
      {
        elid: options.elid,
        func: options.editform,
      },
      options.tab,
    );
  }

  /**
   * Covert value with given converter.
   *
   * @param value - value to convert
   * @param name - name of converter (e.g. 'bytes')
   */
  convertValue(value: string, name: string): Observable<string | undefined> {
    return this.preloadedActionService
      .getAction({
        func: 'convert',
        value,
        name,
      })
      .pipe(map(res => res?.value?.$));
  }
}
